/**
 * ErrorService
 * Servicio centralizado para manejo y logging de errores
 */
import i18next from 'i18next';

class ErrorService {
  /**
   * Log de error con contexto
   */
  static logError(error, context = 'Unknown', additionalInfo = {}) {
    const timestamp = new Date().toISOString();
    const errorInfo = {
      timestamp,
      context,
      message: error?.message || i18next.t('common:messages.error.noMessage'),
      stack: error?.stack,
      ...additionalInfo
    };

    // Desarrollo: Log completo en consola
    if (import.meta.env.DEV) {
      console.group(`❌ Error en ${context} - ${timestamp}`);
      console.error('Mensaje:', errorInfo.message);
      if (error?.stack) console.error('Stack:', error.stack);
      if (Object.keys(additionalInfo).length > 0) {
        console.table(additionalInfo);
      }
      console.groupEnd();
    } else {
      // Producción: Log mínimo
      console.error(`[${context}]`, error?.message || error);
    }

    // Enviar a servicio de monitoreo si está disponible
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          custom: errorInfo
        },
        tags: {
          context
        }
      });
    }

    return errorInfo;
  }

  /**
   * Manejo específico de errores de API
   */
  static handleApiError(error) {
    const status = error.response?.status;
    const message = error.response?.data?.detail || error.message || i18next.t('common:messages.error.connectionError');

    const friendlyMessage = status
      ? (i18next.t(`common:apiErrors.${status}`, { defaultValue: '' }) || message)
      : message;

    this.logError(error, 'API', {
      status,
      endpoint: error.config?.url,
      method: error.config?.method
    });

    return {
      message: friendlyMessage,
      detail: message,
      status,
      isApiError: true
    };
  }

  /**
   * Detectar si es un error de chunk load (Vite stale chunk tras deploy)
   */
  static isChunkLoadError(error) {
    const chunkPatterns = [
      'ChunkLoadError',
      'Failed to fetch dynamically imported module',
      'error loading dynamically imported module',
      'Unable to preload CSS',
      'Importing a module script failed',
    ];
    return chunkPatterns.some(pattern =>
      error?.message?.includes(pattern) ||
      error?.name?.includes(pattern)
    );
  }

  /**
   * Detectar si es un error crítico que requiere reload
   */
  static isCriticalError(error) {
    if (this.isChunkLoadError(error)) return true;

    const criticalPatterns = [
      'NetworkError',
      'SecurityError'
    ];

    return criticalPatterns.some(pattern =>
      error?.message?.includes(pattern) ||
      error?.name?.includes(pattern)
    );
  }

  /**
   * Reload con cache-bust para errores de chunk stale (stale deployment)
   * Usa sessionStorage para evitar reload infinito si el problema persiste.
   */
  static reloadAfterChunkError() {
    const key = 'smarthub_chunk_reload';
    const last = parseInt(sessionStorage.getItem(key) || '0', 10);
    const now = Date.now();
    // Solo reintentamos una vez por minuto para evitar loops
    if (now - last > 60_000) {
      sessionStorage.setItem(key, String(now));
      // Forzar recarga limpia sin usar el bundle cacheado
      window.location.href = window.location.href.split('?')[0] + '?_v=' + now;
    }
  }

  /**
   * Formatear error para mostrar al usuario
   */
  static getUserMessage(error, context = '') {
    if (typeof error === 'string') return error;

    if (error?.response?.data?.detail) {
      return error.response.data.detail;
    }

    if (error?.message) {
      // Filtrar mensajes técnicos
      if (error.message.includes('fetch')) {
        return i18next.t('common:messages.error.fetchError');
      }
      if (error.message.includes('Network')) {
        return i18next.t('common:messages.error.networkRetry');
      }
      return error.message;
    }

    return context
      ? i18next.t('common:messages.error.contextError', { context })
      : i18next.t('common:messages.error.generic');
  }
}

export default ErrorService;
